import { action, computed, observable, toJS } from 'mobx';
import { getSrcElementInputPack } from './utils/InputUtils';
import {
  collectNodes,
  getFlexesForAppend,
  getFlexesForInsert,
  getFlexesForMerge,
  getFlexPrimary,
  getIsAppendNode,
  getIsElementNode,
  getIsNestingContainerNode,
  getIsRootNode,
  getParentContainerNode,
  getParentElementNode,
  getSoleAdjacentElementNode,
} from './utils/NodeUtils';
import {
  makeAreaNode,
  makeElementNode,
  makeSplitterNode,
  makeContainerNodes,
} from './utils/NodeFactory';

const store = {
  // Observables
  nodeTree: observable(makeAreaNode(null)),

  // Computeds
  nodeHash: computed(() => {
    const nodeHash = {};
    const nodes = collectNodes([store.nodeTree], []);
    nodes.forEach((node) => (nodeHash[node.id] = node));
    return nodeHash;
  }),

  nodes: computed(() => Object.values(store.nodeHash.get())),

  // Actions
  addNode: action((node, type, areaInputPack) => {
    // A. Handle root node and return
    const isRoot = getIsRootNode(node);
    if (isRoot) {
      store.transformToContainerNode(node, type, areaInputPack);
      return;
    }

    // B. Handle append node and return
    const nodeHash = store.nodeHash.get();
    const isAppendNode = getIsAppendNode(node, nodeHash, type);
    if (isAppendNode) {
      const parentElementNode = getParentElementNode(node, nodeHash);
      const parentContainerNode = getParentContainerNode(node, nodeHash);
      const idx = parentContainerNode.nodes.findIndex(
        (item) => item.id === parentElementNode.id
      );
      store.updateContainerNode(parentContainerNode, idx, areaInputPack);
      return;
    }

    // C. Handle transform node
    store.transformToContainerNode(node, type, areaInputPack);
  }),

  appendToContainerNode: action(
    (containerNode, splitterPair, areaInputPack) => {
      const { splitterNode, elementNode } = splitterPair;
      const nodes = [...containerNode.nodes, splitterNode, elementNode];
      containerNode.nodes = nodes;
      containerNode.nodeIds = containerNode.nodes.map((n) => n.id);

      // Update flexes
      const flexes = getFlexesForAppend(containerNode, areaInputPack);
      store.updateNodeFlexes(
        containerNode.nodes.filter(getIsElementNode),
        flexes
      );
    }
  ),

  deleteAreaNodeAndRelatedNodes: action((parentElement, containerNode, idx) => {
    // Mutate
    const isHead = idx === 0; // isHead = element->splitter, else splitter->element
    const start = idx + (isHead ? 0 : -1);
    containerNode.nodes.splice(start, 2);
    containerNode.nodeIds = containerNode.nodes.map((n) => n.id);

    // Update flexes
    const elementIdx = Math.ceil(idx / 2); // Map idx to half rounded up to negate the splitter nodes
    const flexes = getFlexesForMerge(parentElement, elementIdx);
    store.updateNodeFlexes(
      containerNode.nodes.filter(getIsElementNode),
      flexes
    );
  }),

  insertInContainerNode: action(
    (containerNode, splitterPair, areaInputPack, idx) => {
      const { splitterNode, elementNode } = splitterPair;
      const isHead = idx === 0;
      const insertionNodes = isHead
        ? [elementNode, splitterNode]
        : [splitterNode, elementNode];
      const start = isHead ? 0 : idx + 1;
      containerNode.nodes.splice(start, 0, ...insertionNodes);
      containerNode.nodeIds = containerNode.nodes.map((n) => n.id);

      // Update flexes
      const elementIdx = Math.ceil(idx / 2); // Map idx to half rounded up to negate the splitter nodes
      const flexes = getFlexesForInsert(
        containerNode,
        areaInputPack,
        elementIdx
      );
      store.updateNodeFlexes(
        containerNode.nodes.filter(getIsElementNode),
        flexes
      );
    }
  ),

  removeNode: action((node, areaInputPack) => {
    if (getIsRootNode(node)) {
      return;
    }

    const nodeHash = store.nodeHash.get();
    const parentElementNode = getParentElementNode(node, nodeHash);
    const parentContainerNode = getParentContainerNode(node, nodeHash);
    const idx = parentContainerNode.nodes.findIndex(
      (item) => item.id === parentElementNode.id
    );

    // A. Handle transformation/translation and return
    const isCollapsable = parentContainerNode.nodes.length === 3;
    if (isCollapsable) {
      const adjacentElementNode = getSoleAdjacentElementNode(
        parentContainerNode.nodes,
        parentElementNode.id
      );

      const isNestingContainer = getIsNestingContainerNode(
        adjacentElementNode,
        nodeHash
      );
      isNestingContainer
        ? store.transformElementNode(adjacentElementNode, parentContainerNode)
        : store.transformToAreaNode(node, parentContainerNode);
      return;
    }

    // B. Handle deletion
    store.deleteAreaNodeAndRelatedNodes(
      areaInputPack.parentElement,
      parentContainerNode,
      idx
    );
  }),

  transformElementNode: action((adjacentElementNode, containerNode) => {
    // A. Transformation target (root)
    const targetElementId = containerNode.meta.parentId;
    const isRootTransformation = targetElementId === null;
    if (isRootTransformation) {
      store.transformToContainerRootNode(adjacentElementNode);
      return;
    }

    // B. Transformation target (non-root)
    const nodeHash = store.nodeHash.get();
    const targetContainerId = nodeHash[targetElementId].meta.parentId;

    // Mutate
    const transferableNodes = adjacentElementNode.nodes[0].nodes;
    transferableNodes.forEach(
      (node) => (node.meta.parentId = targetContainerId)
    );
    const targetContainerNode = nodeHash[targetContainerId];
    const targetIdx = targetContainerNode.nodes.findIndex(
      (node) => node.id === targetElementId
    );
    targetContainerNode.nodes.splice(targetIdx, 1, ...transferableNodes);
    targetContainerNode.nodeIds = targetContainerNode.nodes.map((n) => n.id);
  }),

  transformToContainerRootNode: action((elementNode) => {
    // Transform root
    const containerNodeAsNewRoot = elementNode.nodes[0];
    const { id, meta, nodes, type } = containerNodeAsNewRoot;
    store.nodeTree.id = id;
    store.nodeTree.meta = { ...meta, parentId: null };
    store.nodeTree.nodes = nodes;
    store.nodeTree.nodeIds = nodes.map((n) => n.id);
    store.nodeTree.type = type;
  }),

  transformToAreaNode: action((areaNode, containerNode) => {
    const preservedAreaNode = containerNode.nodes
      .filter(getIsElementNode)
      .map((node) => node.nodes[0])
      .find((node) => node.id !== areaNode.id);

    // Transform mutation
    const isRootNode = getIsRootNode(containerNode);
    const { id, type, meta } = preservedAreaNode;
    containerNode.id = id;
    containerNode.type = type;
    containerNode.meta = {
      ...meta,
      parentId: isRootNode ? null : containerNode.meta.parentId,
    };
    containerNode.nodes = null;
    containerNode.nodeIds = null;

    // Transform mutation of parent
    if (!isRootNode) {
      const targetId = containerNode.meta.parentId;
      store.nodeHash.get()[targetId].nodeIds = [id];
    }
  }),

  transformToContainerNode: action((node, type, areaInputPack) => {
    node.type = type;
    const primaryFlex = getFlexPrimary(node, areaInputPack);
    node.nodes = makeContainerNodes(node.id, primaryFlex);
    node.nodeIds = node.nodes.map((n) => n.id);
  }),

  updateContainerNode: action((containerNode, idx, areaInputPack) => {
    const parentId = containerNode.id;
    const splitterNode = makeSplitterNode(parentId);
    const elementNode = makeElementNode(parentId);
    const isTail = idx === containerNode.nodes.length - 1;
    const splitterPair = { splitterNode, elementNode };
    isTail
      ? store.appendToContainerNode(containerNode, splitterPair, areaInputPack)
      : store.insertInContainerNode(
          containerNode,
          splitterPair,
          areaInputPack,
          idx
        );
  }),

  updateNode: action((syntheticEvent, areaNode, type) => {
    syntheticEvent.preventDefault();

    const areaInputPack = getSrcElementInputPack(syntheticEvent.nativeEvent);

    const isAdd = !syntheticEvent.altKey;
    isAdd
      ? store.addNode(areaNode, type, areaInputPack)
      : store.removeNode(areaNode, areaInputPack);
  }),

  updateNodeFlexes: action((nodes, flexes) =>
    nodes.forEach((node, idx) => (node.meta.flex = flexes[idx]))
  ),
};

export default store;
